import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {Box} from '@mui/material';
import './ReviewAndApprove.scss';
import ReviewAndApproveHeader from './review-and-approve-header/ReviewAndApproveHeader';
import ReviewAndApproveContent from './review-and-approve-content/ReviewAndApproveContent';
import ReviewLeaveWithoutApprovalModal from './review-leave-without-approval-modal/ReviewLeaveWithoutApprovalModal';
import {
  IEditProtocolChildRef,
  useReviewHeaderContext,
} from '../header/ReviewHeader';
import {t} from 'i18next';
import {
  ICreateNewProtocolPayloadProps,
  IssueCode,
  IUpdateProtocolIntelligenceDetailsPayload,
} from '../../../../api/types/aiReview.type';
import {
  useApproveProtocolIntelligence,
  useCreateNewProtocol,
  useUpdateProtocolIntelligenceDetails,
} from '../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../shared/constants';
import CancelEditProtocolIntelligenceModal from '../protocolIntelligence/editProtocolIntelligence/CancelEditProtocolIntelligenceModal';
import SaveAsNewProtocolModal from '../common/JobDetails/SaveAsNewProtocolModal';
import ApproveProtocolModal from './ApproveProtocolModal';

interface IReviewAndApprove {
  setIsEditingProtocol?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReviewAndApprove = forwardRef<IEditProtocolChildRef, IReviewAndApprove>(
  ({setIsEditingProtocol}: IReviewAndApprove, functionRef) => {
    const {
      handleApproveArtifacts,
      setReviewHeaderToast,
      protocolDetails,
      showEditProtocolIntelligence,
      setShowEditProtocolIntelligence,
      clearSearchAndFetchProtocolList,
      protocolVersions,
      isProtocolUpdated,
      setIsProtocolUpdated,
      setIsManageProtocolOpen,
      resetStates,
    } = useReviewHeaderContext();

    const protocolId = protocolDetails?.protocolId;

    // ====================Modal States==============
    const [
      showGenerateArtifactsApprovalModal,
      setShowGenerateArtifactsApprovalModal,
    ] = useState<boolean>(false);
    const [
      showCancelEditProtocolIntelligenceModal,
      setShowCancelEditProtocolIntelligenceModal,
    ] = useState<{open: boolean; triggeredFrom: 'in' | 'out'}>({
      open: false,
      triggeredFrom: 'in',
    }); // triggeredFrom is "out" if user clicks out side of drawer. Show the <CancelEditProtocolIntelligenceModal/> when protocol is in edit state
    const [isSaveAsNewModalOpen, setIsSaveAsNewModalOpen] =
      useState<boolean>(false);
    const [showApproveProtocolModal, setShowApproveProtocolModal] =
      useState<boolean>(false);

    // States from ReviewAndApproveContent
    const [caseContext, setCaseContext] = useState('');
    const [relevanceCriteria, setRelevanceCriteria] = useState('');
    const [issueCodeData, setIssueCodeData] = useState<IssueCode[]>([]);

    const {mutate: approveProtocolIntelligence} =
      useApproveProtocolIntelligence();

    const {mutate: updateProtocolIntelligenceDetails} =
      useUpdateProtocolIntelligenceDetails();

    const {mutate: saveAsNewProtocolDetails} = useCreateNewProtocol();

    const showProtocolsListPage = () => {
      setShowCancelEditProtocolIntelligenceModal((prev) => ({
        ...prev,
        open: false,
      }));
      setShowEditProtocolIntelligence(false);
      setIsEditingProtocol?.(false);
      setIsProtocolUpdated(false);
      clearSearchAndFetchProtocolList();
    };

    const handleShowGenerateArtifactsApprovalModalClose = () => {
      setShowGenerateArtifactsApprovalModal(false);
    };

    const handleApproveCancel = () => {
      setShowGenerateArtifactsApprovalModal(true);
    };

    const handleEditCancelClick = () => {
      if (isProtocolUpdated) {
        setShowCancelEditProtocolIntelligenceModal({
          open: true,
          triggeredFrom: 'in',
        });
        return;
      }
      handleDiscardAndLeaveEditProtocolIntelligence();
    };
    useImperativeHandle(functionRef, () => ({
      handleEditProtocolClose: () =>
        setShowCancelEditProtocolIntelligenceModal({
          open: true,
          triggeredFrom: 'out',
        }),
    }));

    const handleSaveAsNewCall = (protocolName: string) => {
      if (!protocolId) return;
      const payload: ICreateNewProtocolPayloadProps = {
        parentProtocolID: protocolId,
        filename: protocolName,
        caseContext,
        relevance: relevanceCriteria,
        issueCodes: issueCodeData,
      };
      saveAsNewProtocolDetails(payload, {
        onSuccess: () => {
          showProtocolsListPage();
          setReviewHeaderToast({
            isOpen: true,
            message: t('ai.review.protocolIntelligence.saveAsNewSuccessToast'),
            status: ToastType.Success,
          });
        },
        onError: (error) => {
          setReviewHeaderToast({
            isOpen: true,
            message:
              error.message ??
              t('ai.review.protocolIntelligence.isProtocolUpdateFailure'),
            status: ToastType.Error,
          });
        },
      });
    };

    const handleApproveClick = () => {
      setShowApproveProtocolModal(true);
    };

    const handleApprove = () => {
      if (!protocolId) return;
      const payload: IUpdateProtocolIntelligenceDetailsPayload = {
        protocolId,
        isActive: protocolDetails?.isActive,
        caseContext,
        relevanceCriteria,
        issueCodes: issueCodeData,
      };
      // Step 1: Call Edit API
      updateProtocolIntelligenceDetails(payload, {
        onSuccess: () => {
          setIsProtocolUpdated(false);
          // Step 2: Call Approve API
          approveProtocolIntelligence(
            {protocolId},
            {
              onSuccess: () => {
                setReviewHeaderToast({
                  isOpen: true,
                  message: t(
                    'ai.review.protocolIntelligence.protocolApprovedToastSuccess'
                  ),
                  status: ToastType.Success,
                });
                handleApproveArtifacts();
              },
              onError: () => {
                setReviewHeaderToast({
                  isOpen: true,
                  message: t(
                    'ai.review.protocolIntelligence.protocolApprovedToastFailure'
                  ),
                  status: ToastType.Error,
                });
              },
            }
          );
        },
        onError: (error) => {
          setReviewHeaderToast({
            isOpen: true,
            message:
              error?.response?.data?.detail ??
              t('ai.review.protocolIntelligence.isProtocolUpdateFailure'),
            status: ToastType.Error,
          });
        },
      });
    };

    const mapApiValuesToLocalState = () => {
      if (protocolDetails?.caseContext)
        setCaseContext(protocolDetails.caseContext);
      if (protocolDetails?.relevanceCriteria)
        setRelevanceCriteria(protocolDetails.relevanceCriteria);
      if (protocolDetails?.issueCodes?.length)
        setIssueCodeData(
          protocolDetails?.issueCodes.map((code, index) => ({
            id: index.toString(),
            code: code.code,
            question: code.question,
          }))
        );
    };

    useEffect(() => {
      if (protocolDetails?.protocolId) mapApiValuesToLocalState();
    }, [
      protocolDetails?.caseContext,
      protocolDetails?.relevanceCriteria,
      protocolDetails?.issueCodes,
      protocolDetails?.aIstatus,
      protocolDetails?.protocolId,
    ]);

    const handleDiscardAndLeaveEditProtocolIntelligence = () => {
      if (showCancelEditProtocolIntelligenceModal.triggeredFrom === 'out') {
        setIsManageProtocolOpen(false);
        resetStates();
        return;
      }
      mapApiValuesToLocalState();
      setShowCancelEditProtocolIntelligenceModal((prev) => ({
        ...prev,
        open: false,
      }));
      setShowEditProtocolIntelligence(false);
      setIsProtocolUpdated(false);
    };

    const handleSaveAsNewClick = () => {
      setIsSaveAsNewModalOpen(true);
      setShowCancelEditProtocolIntelligenceModal((prev) => ({
        ...prev,
        open: false,
      }));
    };

    const allProtocolVersionsName = useMemo(() => {
      return protocolVersions?.map((protocol) => protocol.protocolName) ?? [];
    }, [protocolVersions]);

    return (
      <Box
        className="review-approve-drawer"
        data-testid="review-approve-container"
      >
        <ReviewAndApproveHeader
          title={
            showEditProtocolIntelligence
              ? protocolDetails?.protocolName || ''
              : t('ai.review.reviewAndApprove.title')
          }
          approveButtonLabel={
            showEditProtocolIntelligence
              ? t('ai.review.protocolIntelligence.editProtocol.saveAsNew')
              : t('ai.review.reviewAndApprove.approve')
          }
          handleCancel={
            showEditProtocolIntelligence
              ? handleEditCancelClick
              : handleApproveCancel
          }
          handleApprove={
            showEditProtocolIntelligence
              ? handleSaveAsNewClick
              : handleApproveClick
          }
        />
        <ReviewLeaveWithoutApprovalModal
          open={showGenerateArtifactsApprovalModal}
          onClose={handleShowGenerateArtifactsApprovalModalClose}
        />
        <ReviewAndApproveContent
          caseContext={caseContext}
          setCaseContext={setCaseContext}
          relevanceCriteria={relevanceCriteria}
          setRelevanceCriteria={setRelevanceCriteria}
          issueCodeData={issueCodeData}
          setIssueCodeData={setIssueCodeData}
        />
        <CancelEditProtocolIntelligenceModal
          showModal={showCancelEditProtocolIntelligenceModal.open}
          handleCancel={handleDiscardAndLeaveEditProtocolIntelligence}
          handleConfirm={handleSaveAsNewClick}
        />
        <SaveAsNewProtocolModal
          open={isSaveAsNewModalOpen}
          onClose={() => setIsSaveAsNewModalOpen(false)}
          onSave={handleSaveAsNewCall}
          description={t(
            'ai.review.protocolIntelligence.editProtocol.modalDescription'
          )}
          textFieldLabel={t(
            'ai.review.protocolIntelligence.editProtocol.protocolNamePlaceholder'
          )}
        />
        <ApproveProtocolModal
          showModal={showApproveProtocolModal}
          handleCancel={() => setShowApproveProtocolModal(false)}
          handleConfirm={handleApprove}
        />
      </Box>
    );
  }
);

export default ReviewAndApprove;
